import { storageKeys } from '@/lib/storage-keys'
import type { Waypoint } from './types'

/**
 * Recent-routes persistence — localStorage-backed, zero backend.
 *
 * Stores the last N calculated routes so a returning user can re-run
 * a previous trip in one click. Small, cheap UX win that makes the
 * app feel personal without introducing accounts.
 *
 * Scope: per-browser, per-device. Clearing browser storage clears
 * history; that's the acceptable privacy tradeoff for a tool that
 * doesn't have accounts. No PII leaves the device.
 *
 * Shape: an array of RecentRoute objects, newest first, capped at
 * MAX_RECENT. Deduped by (originCoord + destCoord + waypointCoords) —
 * the same trip won't appear twice even if the user calculated it
 * from a slightly-different geocoded entry.
 */

const MAX_RECENT = 5

export interface RecentRoute {
	origin: Waypoint
	destination: Waypoint
	waypoints: Waypoint[]
	/** ms-epoch when the route was last calculated */
	calculatedAt: number
}

/** Storage-safe JSON parse — returns [] on any failure. */
function safeParse(raw: string | null): RecentRoute[] {
	if (!raw) return []
	try {
		const parsed = JSON.parse(raw)
		if (!Array.isArray(parsed)) return []
		// Shape-check to defend against an old format ever landing here.
		return parsed.filter(
			(r): r is RecentRoute =>
				!!r &&
				typeof r === 'object' &&
				!!r.origin &&
				!!r.destination &&
				Array.isArray(r.waypoints) &&
				typeof r.calculatedAt === 'number',
		)
	} catch {
		return []
	}
}

/**
 * Deterministic fingerprint of a route. Used for deduplication so the
 * same trip (within 11m coordinate tolerance) doesn't clutter the list.
 */
function fingerprint(r: Pick<RecentRoute, 'origin' | 'destination' | 'waypoints'>): string {
	const coord = (w: Waypoint) => `${w.lng.toFixed(4)},${w.lat.toFixed(4)}`
	return [coord(r.origin), ...r.waypoints.map(coord), coord(r.destination)].join('|')
}

/**
 * Read the recent-routes list. Returns an empty array on SSR or
 * missing/corrupt storage — never throws.
 */
export function loadRecentRoutes(): RecentRoute[] {
	if (typeof window === 'undefined') return []
	try {
		return safeParse(window.localStorage.getItem(storageKeys.recentRoutes()))
	} catch {
		return []
	}
}

/**
 * Push a freshly-calculated route to the front of the list. Dedupes
 * against existing entries with the same fingerprint so only the most
 * recent occurrence is kept. Trims to MAX_RECENT entries.
 *
 * Silent on storage errors (private-mode Safari, quota exceeded, etc.)
 * — this is a nice-to-have; losing it should never affect the app.
 */
export function addRecentRoute(entry: Omit<RecentRoute, 'calculatedAt'>): void {
	if (typeof window === 'undefined') return
	const existing = loadRecentRoutes()
	const newEntry: RecentRoute = { ...entry, calculatedAt: Date.now() }
	const fp = fingerprint(newEntry)
	const deduped = existing.filter((r) => fingerprint(r) !== fp)
	const next = [newEntry, ...deduped].slice(0, MAX_RECENT)
	try {
		window.localStorage.setItem(storageKeys.recentRoutes(), JSON.stringify(next))
	} catch {
		// Private mode / quota exhausted — acceptable to drop silently
	}
}

/** Clear the recent-routes list entirely. */
export function clearRecentRoutes(): void {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.removeItem(storageKeys.recentRoutes())
	} catch {
		// Storage unavailable — nothing to do
	}
}

/**
 * Human-readable relative time ("2h ago", "yesterday"). Lightweight
 * — this is a small-label touch, not a real i18n task.
 */
export function formatRelative(ms: number): string {
	const ageMs = Date.now() - ms
	const minutes = Math.round(ageMs / 60000)
	if (minutes < 1) return 'just now'
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.round(minutes / 60)
	if (hours < 24) return `${hours}h ago`
	const days = Math.round(hours / 24)
	if (days === 1) return 'yesterday'
	if (days < 7) return `${days}d ago`
	return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Short label for a waypoint — first segment of the place_name. */
export function shortWaypointLabel(w: Waypoint): string {
	const first = w.name.split(',')[0]?.trim() ?? w.name
	return first.length > 18 ? `${first.slice(0, 16)}…` : first
}
